import { useEffect, useState } from 'react'
import { AppLayout } from '../../App/Layout/AppLayout'
import {
  Box,
  useTheme,
  Flex,
  Text,
  Avatar,
  Icon,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton
} from '@homebotapp/hb-react-component-catalog'
import topographicalMapLight from '../../assets/homes/img/topo-bg-light.png'
import topographicalMapDark from '../../assets/homes/img/topo-bg-wide.svg'
import { fetchClientHomes, setCurrentClient } from '../../actions/client'
import { fetchClientData } from '../../actions/client'
import {
  selectClientHomes,
  selectClientIsLoadingData,
  selectLoadingHomes,
  selectFromClientData
} from '../../store/selectors/client'
import { useDispatch, useSelector } from 'react-redux'
import { fetchProfile } from '../../actions/customerProfile'
import { useGetClientId } from '../../hooks/useGetClientId'
import { setIsShowingNativeAppCtaModal } from '../../store/slices/modals'
import { useIntl } from 'react-intl'
import SharePanel from '../../Components/shared/SharePanel/SharePanel'
import TeamPanel from '../../Components/shared/TeamPanel/TeamPanel'
import EmailPreferencesPanel from '../../Components/shared/PreferencesPanel/ManagePreferences/ManagePreferences'
import {
  selectCustomerProfileLoading,
  selectHomebotNetworkEnabled,
  selectNativeAccessFeature
} from '../../store/selectors/customerProfile'
import { setActiveNavigationTab } from '../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../types/navigation'
import { IconName } from '@homebotapp/hb-react-component-catalog'
import { OwnAHome } from '../../Components/homeSearch/ListingDetails/OwnAHome/OwnAHome'
import { useCanViewHbnTeamPage } from '../../hooks/useCanViewHbnTeamPage'
import { RowMenuItem } from '../../Components/shared/RowMenuItem/RowMenuItem'
import history from '../../history'
import { BuyerInfo } from '../../types/buyerInfo'
import { selectBuyerInfo } from '../../store/selectors/buyerInfo'

const MSG = {
  profile: {
    id: 'Profile.title',
    defaultMessage: 'Profile'
  },
  searchProfile: {
    id: 'Profile.searchProfile.title',
    defaultMessage: 'Search Profile'
  },
  yourDetails: {
    id: 'Profile.yourDetails.title',
    defaultMessage: 'Your Details'
  },
  yourTeam: {
    id: 'Profile.yourDetails.yourTeam.title',
    defaultMessage: 'Your Team'
  },
  emailPreferences: {
    id: 'Profile.yourDetails.emailPreferences.title',
    defaultMessage: 'Email Preferences'
  },
  quickLinks: {
    id: 'Profile.quickLinks.title',
    defaultMessage: 'Quick Links'
  },
  favoriteHomes: {
    id: 'Profile.quickLinks.favoriteHomes.title',
    defaultMessage: 'Your favorite homes'
  },
  savedSearches: {
    id: 'Profile.quickLinks.savedSearches.title',
    defaultMessage: 'Your saved searches'
  },
  shareHomebot: {
    id: 'Profile.quickLinks.shareHomebot.title',
    defaultMessage: 'Share Homebot'
  },
  getTheApp: {
    id: 'Profile.quickLinks.getTheApp.title',
    defaultMessage: 'Get the app'
  }
}

export const Profile = ({ match }) => {
  const intl = useIntl()
  const [showSharePanel, setShowSharePanel] = useState(false)
  const [showTeamPanel, setShowTeamPanel] = useState(false)
  const [showEmailPreferencesPanel, setShowEmailPreferencesPanel] = useState(false)
  const { theme } = useTheme()
  const dispatch = useDispatch()
  const clientId = useGetClientId()
  const { customerProfileId } = match.params
  const isLoadingClientData = useSelector(selectClientIsLoadingData)
  const clientFirstName = useSelector(selectFromClientData('firstName'))
  const clientLastName = useSelector(selectFromClientData('lastName'))
  const clientEmail = useSelector(selectFromClientData('email'))
  const homes = useSelector(selectClientHomes)
  const isLoadingHomes = useSelector(selectLoadingHomes)
  const profileLoading = useSelector(selectCustomerProfileLoading)
  const isNativeAccessEnabled = useSelector(selectNativeAccessFeature)
  const canViewHbnTeamPage = useCanViewHbnTeamPage()
  const formatHomeAddress = address => {
    return `${address?.city ? `${address?.city}, ` : ''}${address?.state ? `${address?.state} ${address?.zip}` : address?.zip
      }`
  }
  const quickLinks = [
    {
      title: intl.formatMessage(MSG.favoriteHomes),
      isVisible: true,
      icon: 'heart-empty',
      href: `/home-search/${customerProfileId}/favorite-listings?clientId=${clientId}`
    },
    {
      title: intl.formatMessage(MSG.savedSearches),
      isVisible: true,
      icon: 'bookmark-magnifying-glass',
      href: `/home-search/${customerProfileId}/saved-searches`
    },
    {
      title: intl.formatMessage(MSG.shareHomebot),
      isVisible: true,
      icon: 'share-arrow',
      onClick: () => setShowSharePanel(true)
    },
    {
      title: intl.formatMessage(MSG.getTheApp),
      isVisible: isNativeAccessEnabled,
      icon: 'mobile',
      onClick: () => dispatch(setIsShowingNativeAppCtaModal(true))
    }
  ]

  useEffect(() => {
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.PROFILE))
    if (customerProfileId) {
      dispatch(fetchProfile(customerProfileId))
    }
  }, [dispatch, customerProfileId])

  useEffect(() => {
    if (clientId) {
      dispatch(setCurrentClient(clientId))
      dispatch(fetchClientData(clientId, customerProfileId))
      dispatch(fetchClientHomes(clientId))
      dispatch(fetchProfile(customerProfileId))
    }
  }, [dispatch, clientId, customerProfileId])

  return (
    <AppLayout loading={isLoadingClientData || isLoadingHomes || profileLoading} enableAgentDropdown match={match}>
      <Box
        height='21vh'
        backgroundImage={`url(${theme.isLightThemeType ? topographicalMapLight : topographicalMapDark})`}
        backgroundSize='cover'
        backgroundBlendMode='multiply'
        backgroundColor='whiteAlpha.50'
        _dark={{
          backgroundColor: 'blackAlpha.600'
        }}
        display={['none', 'none', 'flex']}
        alignItems='center'
        justifyContent='center'
      >
        <SubContainer>
          <Text size='3xl' fontWeight={600} color='neutral.900' mt={6}>
            {intl.formatMessage(MSG.profile)}
          </Text>
        </SubContainer>
      </Box>
      <Container>
        <Box
          backgroundColor={theme.isLightThemeType ? 'brand.300' : 'brand.200'}
          width='100%'
          minHeight='76px'
          alignContent='center'
          px={6}
          mt={[0, 0, 8]}
        >
          <Flex flexDirection='row' gap={4}>
            <Avatar src='https://via.placeholder.com/150' />
            <Flex flexDirection='column'>
              <Text size='sm' fontWeight={700} color='white' m={0}>
                {clientFirstName} {clientLastName}
              </Text>
              <Text size='2xs' fontWeight={400} color='white' m={0}>
                {clientEmail}
              </Text>
            </Flex>
          </Flex>
        </Box>
        <Flex flexDirection={['column', 'column', 'row']} justifyContent='space-between' py={4} px={[4, 4, 0]} gap={8}>
          <Box flex={1}>
            <Text size='sm' fontWeight={700} color='neutral.900'>
              {intl.formatMessage(MSG.yourDetails)}
            </Text>
            <Flex flexDirection='column' gap={2}>
              {homes && homes.length === 0 && <OwnAHome />}
              {homes &&
                homes.length > 0 &&
                homes.map(home => (
                  <Link
                    key={home.id}
                    href={`/reports/${clientId}/home/${home.id}`}
                    color='neutral.800'
                    tracking={{
                      guid: '6kyL4W8NuKz7y5aJtbxSnM',
                      ui_context: 'Profile',
                      descriptor: 'Home link - ' + home.address.street
                    }}
                  >
                    <Flex
                      data-testid={`home-${home.id}`}
                      flexDirection='row'
                      gap={4}
                      background={theme.isLightThemeType ? 'neutral.300' : 'neutral.200'}
                      minHeight='72px'
                      alignItems='center'
                      px={4}
                    >
                      <Icon name='home' width='7' height='7' />
                      <Flex flexDirection='column'>
                        <Text size='xs' fontWeight={600} color='neutral.900' m={0}>
                          {home.address.street}
                        </Text>
                        <Text size='xs' fontWeight={600} color='neutral.900' m={0}>
                          {formatHomeAddress(home.address)}
                        </Text>
                      </Flex>
                      <Flex flex={1} justifyContent='flex-end'>
                        <Icon name='angle-right' width='4' height='4' color='primary.700' />
                      </Flex>
                    </Flex>
                  </Link>
                ))}
              {canViewHbnTeamPage && (
                <RowMenuItem
                  icon={<Icon name='torso-business' width='7' height='7' />}
                  label={intl.formatMessage(MSG.yourTeam)}
                  onClick={() => setShowTeamPanel(true)}
                  tracking={{
                    guid: 'ktFsviyfFfoDEADFyKp2WE',
                    ui_context: 'Profile',
                    descriptor: 'Your team link'
                  }}
                />
              )}

              <RowMenuItem
                icon={<Icon name='search2-filled' width='7' height='7' />}
                label={intl.formatMessage(MSG.searchProfile)}
                onClick={() => {
                  history.push(`/search-profile/${customerProfileId}`, { customerProfileId })
                }}
                tracking={{
                  guid: 'n87b7WV2ogqr8wwKKAWhj2',
                  ui_context: 'Search_Profile',
                  descriptor: 'search profile view'
                }}
              />

              <RowMenuItem
                icon={<Icon name='bell-filled' width='7' height='7' />}
                label={intl.formatMessage(MSG.emailPreferences)}
                onClick={() => setShowEmailPreferencesPanel(true)}
                tracking={{
                  guid: 'n87b7WV2ogqr8vwKKAWhj2',
                  ui_context: 'Profile',
                  descriptor: 'Email preferences link'
                }}
              />
            </Flex>
          </Box>
          <Box flex={1}>
            <Text size='sm' fontWeight={700} color='neutral.900'>
              {intl.formatMessage(MSG.quickLinks)}
            </Text>
            <Flex flexDirection='column' gap={2}>
              {quickLinks
                .filter(link => link.isVisible)
                .map(link => (
                  <Link
                    key={link.title}
                    href={link.href}
                    onClick={link.onClick}
                    tracking={{
                      guid: 'r4tE3ZuqSf3rzFNnLD177B',
                      ui_context: 'Profile',
                      descriptor: 'Quick link - ' + link.title
                    }}
                  >
                    <Flex flexDirection='row' gap={4} minHeight='32px' alignItems='center' px={4}>
                      <Icon name={link.icon as IconName} width='7' height='7' color='neutral.900' />
                      <Text size='xs' fontWeight={600} color='neutral.900' m={0}>
                        {link.title}
                      </Text>
                      <Flex flex={1} justifyContent='flex-end'>
                        <Icon name='angle-right' width='4' height='4' color='primary.700' />
                      </Flex>
                    </Flex>
                  </Link>
                ))}
            </Flex>
          </Box>
        </Flex>
      </Container>
      <ModalWrapper isOpen={showSharePanel} onClose={() => setShowSharePanel(false)}>
        <SharePanel />
      </ModalWrapper>
      <ModalWrapper isOpen={showTeamPanel} onClose={() => setShowTeamPanel(false)}>
        <Box py={4}>
          <TeamPanel />
        </Box>
      </ModalWrapper>
      <ModalWrapper isOpen={showEmailPreferencesPanel} onClose={() => setShowEmailPreferencesPanel(false)}>
        <Box p={8}>
          <EmailPreferencesPanel />
        </Box>
      </ModalWrapper>
    </AppLayout>
  )
}

const ModalWrapper = ({ children, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent textAlign='center' overflow='hidden'>
        <ModalCloseButton zIndex={1000} />
        {children}
      </ModalContent>
    </Modal>
  )
}

export const Container = ({ children }) => {
  return (
    <Box display='flex' justifyContent='center' alignItems='center' pb={16}>
      <SubContainer>{children}</SubContainer>
    </Box>
  )
}

export const SubContainer = ({ children }) => {
  return (
    <Flex flexDirection='column' gap={[0, 0, 6]} width={['100%', '100%', '60%']}>
      {children}
    </Flex>
  )
}
